/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { withoutLegacyLeadAdUrl } from './ad-dialog-helpers';

/** Something the agency must fill in before it can create the ad (Housien supplies none of it). */
export type MissingAdSetting = 'privacyUrl' | 'websiteUrl' | 'dailyBudget' | 'duration';

/** A platform's ad readiness: what is still missing for a Lead Ad and for a boost. */
export interface PlatformAdReadiness {
  leadAd: MissingAdSetting[];
  boost: MissingAdSetting[];
}

/** The agency's ad readiness on each platform. */
export interface AdReadiness {
  facebook: PlatformAdReadiness;
  instagram: PlatformAdReadiness;
}

/** What nothing configured looks like: everything missing. */
export const NOTHING_CONFIGURED: AdReadiness = {
  facebook: { leadAd: ['privacyUrl', 'websiteUrl', 'dailyBudget', 'duration'], boost: ['dailyBudget', 'duration'] },
  instagram: { leadAd: ['privacyUrl', 'websiteUrl', 'dailyBudget', 'duration'], boost: ['dailyBudget', 'duration'] },
};

/** Whether the value is a positive integer. */
function isPositiveWhole(value: unknown): boolean {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

/**
 * The saved daily budget in cents, or 0 when the agency has not set one. There is
 * no built-in amount: spending is the agency's own decision.
 *
 * @param value - The settings field as received.
 * @returns Cents, or 0 when not saved.
 */
export function savedBudgetCents(value: unknown): number {
  return isPositiveWhole(value) ? (value as number) : 0;
}

/**
 * The saved run length in days, or 0 when the agency has not set one.
 *
 * @param value - The settings field as received.
 * @returns Days, or 0 when not saved.
 */
export function savedDurationDays(value: unknown): number {
  return isPositiveWhole(value) ? (value as number) : 0;
}

/** Whether a stored link is the agency's own (not empty, not one of Housien's retired defaults). */
function hasOwnUrl(value: unknown): boolean {
  return withoutLegacyLeadAdUrl(typeof value === 'string' ? value : '') !== '';
}

/** Which budget/duration items a platform is still missing. */
function missingAmounts(
  data: Record<string, unknown>, budgetKey: string, durationKey: string,
): MissingAdSetting[] {
  const missing: MissingAdSetting[] = [];
  if (!isPositiveWhole(data[budgetKey])) missing.push('dailyBudget');
  if (!isPositiveWhole(data[durationKey])) missing.push('duration');
  return missing;
}

/**
 * What is still missing, on each platform, before a Lead Ad or a boost can be created.
 * Mirrors the server's rule (`ad_settings_requirements`), which is the one that enforces
 * it; this only tells the user early.
 *
 * @param data - The agency settings payload.
 * @returns The missing items per platform and kind of ad.
 */
export function adReadinessFrom(data: Record<string, unknown> | null | undefined): AdReadiness {
  if (!data) return NOTHING_CONFIGURED;
  const links: MissingAdSetting[] = [];
  if (!hasOwnUrl(data.lead_ad_privacy_url)) links.push('privacyUrl');
  if (!hasOwnUrl(data.lead_ad_website_url)) links.push('websiteUrl');
  const facebook = missingAmounts(data, 'fb_lead_ad_budget', 'fb_lead_ad_duration');
  const instagram = missingAmounts(data, 'ig_lead_ad_budget', 'ig_lead_ad_duration');
  return {
    facebook: { leadAd: [...links, ...facebook], boost: facebook },
    instagram: { leadAd: [...links, ...instagram], boost: instagram },
  };
}
